import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { formations } from "@/data/formations";

const navItems = [
  { to: "/", label: "Accueil" },
  { to: "/formations", label: "Formations" },
  { to: "/guide-ia", label: "Guide IA" },
  { to: "/blog", label: "Blog" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formationsOpen, setFormationsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setFormationsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-brand-line"
          : "bg-brand-page border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src="/logo-yaformis.png" alt="YAFORMIS" className="h-14 md:h-16 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
          {navItems.map((item) => {
            if (item.to === "/formations") {
              return (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setFormationsOpen(true)}
                  onMouseLeave={() => setFormationsOpen(false)}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `px-4 py-2 text-sm font-semibold transition-colors ${
                        isActive ? "text-brand-blue" : "text-brand-ink hover:text-brand-blue"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {formationsOpen && (
                    <div className="absolute top-full left-0 mt-1 w-[480px] bg-white border border-brand-line shadow-2xl p-3">
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-brand-blue px-3 py-2">
                        Catalogue
                      </div>
                      <div className="grid gap-1">
                        {formations.map((f) => (
                          <Link
                            key={f.slug}
                            to={`/formations/${f.slug}`}
                            data-testid={`nav-formation-${f.slug}`}
                            className="flex items-start gap-3 p-3 hover:bg-brand-surface group transition-colors"
                          >
                            <span className="num-marker pt-0.5">{f.number}</span>
                            <div className="flex-1">
                              <div className="font-heading font-semibold text-sm text-brand-ink">{f.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{f.duration} · {f.level}</div>
                            </div>
                            <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-brand-blue transition-colors" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "text-brand-blue" : "text-brand-ink hover:text-brand-blue"
                  }`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden md:inline-flex btn-cta text-sm"
            style={{ padding: "10px 20px" }}
          >
            Demander un devis
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-brand-ink"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-line" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 border-b border-brand-line text-base font-semibold ${
                    isActive ? "text-brand-blue" : "text-brand-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-4 pl-2 text-[10px] font-bold tracking-[0.2em] uppercase text-brand-blue">Formations</div>
            {formations.map((f) => (
              <Link
                key={f.slug}
                to={`/formations/${f.slug}`}
                data-testid={`mobile-nav-formation-${f.slug}`}
                className="py-3 pl-2 border-b border-brand-line text-sm font-medium text-brand-ink"
              >
                <span className="num-marker mr-3">{f.number}</span>
                {f.title}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="btn-cta justify-center mt-5"
            >
              Demander un devis
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
